import { Search, StarBorderOutlined } from "@mui/icons-material"
import { Box } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import intl from "src/internationalization/intl"
import {
    Button,
    CircularProgress,
    Grid,
    InnerSection,
    ObjectiveCard,
    ObjectiveMonitoringPanel,
    ObjectiveMonitorTrigger,
    Section,
    Select,
    TextInput,
    Typography,
} from "src/lib"
import { isManagerProfile } from "src/lib/utils/isManagerProfile"
import type { IOpportunity } from "src/services/api/opportunities/types"
import { useUserStore } from "src/store/userStore"
import { useOpportunitiesFilters } from "./useOpportunitiesFilters"
import { useOpportunitiesSection } from "./useOpportunitiesSection"
import { getObjectiveListKey } from "./objectiveListKey"
import type { IOpportunitiesSectionProps } from "./types"

const OpportunitiesSection = ({
    showMonitoringPanel = false,
    showCreateObjectiveButton = true,
}: IOpportunitiesSectionProps = {}) => {
    const { user } = useUserStore()
    const canCreateObjectives = isManagerProfile(user?.profile)
    const [selectedMonitoringKey, setSelectedMonitoringKey] = useState<string | null>(null)
    const monitoringPanelRef = useRef<HTMLDivElement | null>(null)

    const {
        sortDirection,
        clientsImpactedFilter,
        validityFilter,
        sortOptions,
        clientsImpactedOptions,
        validityFilterOptions,
        filters,
        handleSortChange,
        handleClientsImpactedChange,
        handleValidityFilterChange,
    } = useOpportunitiesFilters()

    const {
        objectives,
        isLoading,
        error,
        showAll,
        visibleObjectives,
        handleOpenNewObjectiveModal,
        formatAddedAt,
        formatDueDate,
        toggleShowAll,
        handleClickAIButton,
    } = useOpportunitiesSection(filters)

    const selectedObjective = useMemo(
        () =>
            selectedMonitoringKey
                ? visibleObjectives.find((o) => getObjectiveListKey(o) === selectedMonitoringKey) ??
                  objectives.find((o) => getObjectiveListKey(o) === selectedMonitoringKey)
                : undefined,
        [selectedMonitoringKey, visibleObjectives, objectives]
    )

    useEffect(() => {
        if (!selectedMonitoringKey || !monitoringPanelRef.current) return
        monitoringPanelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }, [selectedMonitoringKey])

    const toggleMonitoring = (listKey: string) => {
        setSelectedMonitoringKey((prev) => (prev === listKey ? null : listKey))
    }

    const renderObjectiveCard = (objective: IOpportunity) => {
        const listKey = getObjectiveListKey(objective)
        const monitoringActive = selectedMonitoringKey === listKey

        return (
            <ObjectiveCard
                compact
                fullWidth
                selected={monitoringActive}
                title={objective.title}
                objectiveId={objective.objective_id}
                recommendation={objective.text}
                addedAt={formatAddedAt(objective.createdDate)}
                dueDate={formatDueDate(objective.daysOfValid)}
                impactedClients={String(objective.impacted_clients?.length ?? 0)}
                impactedClientsArray={objective.impacted_clients}
                context={objective.title}
                showImpactedClients={true}
                monitorButton={
                    showMonitoringPanel && objective.has_movements_cypher ? (
                        <ObjectiveMonitorTrigger
                            active={monitoringActive}
                            onClick={() => toggleMonitoring(listKey)}
                        />
                    ) : undefined
                }
            />
        )
    }

    return (
        <Section
            title={intl("opportunities")}
            icon={<StarBorderOutlined />}
            headerComponents={
                <>
                    {canCreateObjectives && showCreateObjectiveButton && (
                        <Button variant="contained" onClick={handleOpenNewObjectiveModal}>
                            <Typography>{intl("addNewOpportunity")}</Typography>
                        </Button>
                    )}
                    <TextInput
                        placeholder={intl("search")}
                        fullWidth={false}
                        startAdornment={<Search />}
                    />
                </>
            }
            onClickAIButton={handleClickAIButton}
        >
            <Grid container justifyContent="space-between">
                <Grid item></Grid>
                <Grid item>
                    <Grid container gap={2}>
                        <Select
                            value={sortDirection}
                            onChange={handleSortChange}
                            options={sortOptions}
                            size="small"
                        />
                        <Select
                            value={clientsImpactedFilter}
                            onChange={handleClientsImpactedChange}
                            options={clientsImpactedOptions}
                            size="small"
                        />
                        <Select
                            value={validityFilter}
                            onChange={handleValidityFilterChange}
                            options={validityFilterOptions}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </Grid>
            <InnerSection title={intl("Todas as oportunidades")}>
                <Grid container spacing={1.5} sx={{ mt: 2 }}>
                    {isLoading && (
                        <Grid container justifyContent="center" sx={{ py: 4, width: "100%" }}>
                            <CircularProgress color="primary" />
                        </Grid>
                    )}

                    {error && !isLoading && (
                        <Typography variant="body2" color="secondary">
                            {error}
                        </Typography>
                    )}

                    {!isLoading &&
                        !error &&
                        visibleObjectives.map((objective) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={getObjectiveListKey(objective)}>
                                {renderObjectiveCard(objective)}
                            </Grid>
                        ))}
                </Grid>

                {showMonitoringPanel && selectedObjective && (
                    <Box ref={monitoringPanelRef} sx={{ mt: 3 }}>
                        <ObjectiveMonitoringPanel
                            variant="standalone"
                            objectiveTitle={selectedObjective.title}
                            onClose={() => setSelectedMonitoringKey(null)}
                            movementPortfolioRollups={selectedObjective.movement_portfolio_rollups}
                            movementSummary={selectedObjective.summary}
                            objectiveWarnings={selectedObjective.warnings}
                            impactedClients={selectedObjective.impacted_clients}
                        />
                    </Box>
                )}
            </InnerSection>
            {objectives.length > 4 && (
                <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button variant="outlined" color="neutral" onClick={toggleShowAll}>
                        {showAll ? intl("showLessOpportunities") : intl("showMoreOpportunities")}
                    </Button>
                </Grid>
            )}
        </Section>
    )
}

export { OpportunitiesSection }
